import * as THREE from 'three';
import { ball, mesh, random, tube } from './art';

export const INSECT_HANGOUTS = [
  { id: 'cafe-friends', name: 'The crumb club', x: -88, z: 45 },
  { id: 'garden-friends', name: 'Lotus lookout', x: -22, z: 38 },
  { id: 'park-friends', name: 'The wandering seed', x: 216, z: 24 },
];
export const LOTUS_LANDINGS = [{ x: -20.5, z: 38 }, { x: -24, z: 31 }, { x: -31, z: 28 }, { x: -20, z: 24 }];
const CHATS = [
  [['Pip', 'This crumb has a nice side.'], ['Bean', 'Which side?'], ['Pip', 'The crumb side.'], ['Bean', 'Excellent choice.']],
  [['Bean', 'Big plans today?'], ['Pip', 'Sit here. Maybe there.'], ['Bean', 'Leave some time for here.'], ['Pip', 'Already booked.']],
  [['Pip', 'I named this leaf Kevin.'], ['Bean', 'Does Kevin know?'], ['Pip', 'We are taking it slowly.']],
  [['Bean', 'That cloud looks like a potato.'], ['Pip', 'You said that yesterday.'], ['Bean', 'Reliable cloud.']],
  [['Pip', 'You have a little dust on you.'], ['Bean', 'That is my outdoor outfit.'], ['Pip', 'Very seasonal.']],
  [['Bean', 'Is it time for a nap?'], ['Pip', 'Let me check.'], ['Pip', 'Yes. Still yes.']],
];
const FLYER_CHATS = [
  ['Lovely landing. Nobody saw the first attempt.', 'You are very small. Respectfully.', 'That leaf is reserved. By me. For sitting.'],
  ['Hello down there!', 'I flew all this way to sit on a different leaf.', 'Worth it. Excellent leaf.'],
  ['The pond is enormous from up here.', 'Would you like a little company?', 'I can fly slowly. Probably.'],
];
const COLLECTOR_CHATS = [
  ['Nearly there.', 'Where? Excellent question.', 'Haven\'t decided.'],
  ['You look like someone who appreciates a seed.', 'I was taking it somewhere.', 'The somewhere can wait.'],
  ['That butterfly has no luggage.', 'A bold approach.', 'I am keeping my seed.'],
];
export interface InsectSpeech { name: string; text: string; position: THREE.Vector3; }

const LADYBIRD_COLORS = ['#cd5548', '#e7aa42', '#b674a6', '#dd7951', '#dd7e99'];
function insect(parent: THREE.Group, ladybird: boolean, seed: number, color: string) {
  const variation = random(seed);
  const group = new THREE.Group(); parent.add(group);
  if (ladybird) group.scale.setScalar(.86 + variation() * .3);
  const body = new THREE.Group(); group.add(body);
  const wings: THREE.Group[] = [];
  if (ladybird) for (const sign of [-1, 1]) {
    const wing = new THREE.Group(); wing.position.set(sign * .3, .55, .15); body.add(wing); wings.push(wing);
    const membrane = mesh(new THREE.SphereGeometry(1, 16, 8), new THREE.MeshStandardMaterial({ color: '#e5f3e3', transparent: true, opacity: .72, roughness: .6 }), wing, sign * .6, 0, .3);
    membrane.scale.set(.8, .035, 1);
    wing.visible = false;
  }
  ball(body, 0, .42, .1, .65, .43, .85, ladybird ? color : '#4f8b83');
  tube(body, [new THREE.Vector3(0, .65, -.5), new THREE.Vector3(0, .86, .1), new THREE.Vector3(0, .55, .9)], .025, '#334644');
  if (ladybird) {
    const rows = 2 + Math.floor(variation() * 3);
    for (let i = 0; i < rows; i++) for (const sign of [-1, 1]) {
      const z = -.38 + i * .95 / Math.max(1, rows - 1) + (variation() - .5) * .07;
      const x = sign * (.26 + variation() * .12);
      const y = .42 + .43 * Math.sqrt(Math.max(0, 1 - (x / .65) ** 2 - ((z - .1) / .85) ** 2));
      const size = .075 + variation() * .05;
      const spot = ball(body, x, y, z, size, .025, size * (1 + variation() * .2), '#33403d');
      spot.rotation.z = -sign * .3;
    }
  }
  else for (const sign of [-1, 1]) tube(body, [new THREE.Vector3(sign * .28, .78, -.3), new THREE.Vector3(sign * .35, .78, .35)], .018, '#91b7a0');
  ball(body, 0, .4, -.62, .4, .32, .35, '#3e504b');
  for (const sign of [-1, 1]) {
    ball(body, sign * .19, .58, -.85, .13, .13, .1, '#f9f3df');
    ball(body, sign * .19, .58, -.94, .055, .065, .035, '#283934');
    tube(body, [new THREE.Vector3(sign * .17, .6, -.69), new THREE.Vector3(sign * .32, .93, -.87), new THREE.Vector3(sign * .46, .91, -.93)], .022, '#3e504b');
  }
  const legs: THREE.Group[] = [];
  for (let i = 0; i < 3; i++) for (const sign of [-1, 1]) {
    const leg = new THREE.Group(); leg.position.set(sign * .42, .3, -.3 + i * .4); group.add(leg);
    tube(leg, [new THREE.Vector3(), new THREE.Vector3(sign * .38, -.07, .08), new THREE.Vector3(sign * .48, -.26, .18)], .035, '#485b4d'); legs.push(leg);
  }
  // A small highlight keeps the beetle's shell readable at ant height.
  if (!ladybird) { const glint = mesh(new THREE.SphereGeometry(.1, 8, 5), '#a0c6ab', body, -.28, .8, .1); glint.scale.set(1, .25, 2.5); }
  return { group, body, legs, wings };
}

export class FriendlyInsects {
  private readonly pairs;
  private readonly seedObject: THREE.Mesh;
  private clock = 0;
  private lastTime = 0;
  private soloBegan = -100;
  private soloLines: string[] = [];
  private escortUntil = 0;
  private escortPending = false;
  private readonly soloVisits = [0, 0, 0];
  readonly state = { flyerMode: 'perched', collectorMode: 'rolling' };
  private active = -1;
  private began = 0;
  private nextChat = 0;
  private conversation = 0;
  private readonly visits = INSECT_HANGOUTS.map(() => 0);
  speech: InsectSpeech | undefined;

  constructor(parent: THREE.Group, seed = 42) {
    const paletteOffset = Math.floor(random(seed)() * LADYBIRD_COLORS.length);
    this.pairs = INSECT_HANGOUTS.map((p, n) => {
      const color = LADYBIRD_COLORS[(paletteOffset + n) % LADYBIRD_COLORS.length];
      const pair = [insect(parent, true, (seed + n * 7919) >>> 0, color)];
      if (n === 0) pair.push(insect(parent, false, seed, color));
      pair.forEach((bug, i) => bug.group.position.set(p.x + (n === 0 ? (i ? 1.4 : -1.4) : 0), n === 1 ? .18 : 0, p.z));
      return pair;
    });
    this.seedObject = mesh(new THREE.DodecahedronGeometry(.32), '#ae7950', parent, 216, .32, 22.8);
  }
  interaction(x: number, z: number): string | undefined {
    for (const n of [1, 2]) {
      const p = this.pairs[n][0].group.position;
      if (p.y < .5 && Math.hypot(p.x - x, p.z - z) < 3) return n === 1 ? 'Say hello to Dot' : 'Talk to Pebble';
    }
    return undefined;
  }
  greet(x: number, z: number): boolean {
    if (!this.interaction(x, z)) return false;
    const n = Math.hypot(this.pairs[1][0].group.position.x - x, this.pairs[1][0].group.position.z - z) < 3 ? 1 : 2;
    this.active = n;
    this.startSolo(n);
    if (n === 1) this.escortPending = true;
    return true;
  }
  private startSolo(n: number): void {
    const scripts = n === 1 ? FLYER_CHATS : COLLECTOR_CHATS;
    this.soloLines = scripts[this.soloVisits[n]++ % scripts.length];
    this.soloBegan = this.clock;
    this.nextChat = this.clock + this.soloLines.length * 3.8 + 12;
  }
  update(time: number, x: number, z: number): void {
    const dt = Math.min(.1, Math.max(0, time - this.lastTime)); this.lastTime = time; this.clock = time;
    let nearest = -1; let distance = 10;
    INSECT_HANGOUTS.forEach((p, i) => { const d = Math.hypot(p.x - x, p.z - z); if (d < distance) { nearest = i; distance = d; } });
    if (nearest !== this.active) { this.active = nearest; this.nextChat = time + 1.2; this.began = -100; this.soloBegan = -100; }
    if (nearest === 0 && time >= this.nextChat) {
      this.conversation = (nearest * 2 + this.visits[nearest]++) % CHATS.length;
      this.began = time; this.nextChat = time + CHATS[this.conversation].length * 3.8 + 12;
    }
    const lineIndex = Math.floor((time - this.began) / 3.8);
    const line = nearest === 0 ? CHATS[this.conversation][lineIndex] : undefined;
    this.speech = undefined;
    this.pairs[0].forEach((bug, i) => {
      const n = 0;
      const p = INSECT_HANGOUTS[n]; const chatting = n === nearest && !!line;
      const clock = time + n * 4 + i * 3; const phase = clock % 17;
      const walking = !chatting && phase < 4;
      const wander = Math.floor(clock / 17) * 4 + Math.min(phase, 4);
      bug.group.position.set(p.x + (i ? 1.4 : -1.4) + (chatting ? 0 : Math.sin(wander * .35 + i) * .45), 0, p.z + (chatting ? 0 : Math.cos(wander * .35 + n) * .4));
      bug.group.rotation.y = i ? Math.PI / 2 : -Math.PI / 2;
      const speaking = chatting && (line[0] === 'Pip' ? i === 0 : i === 1);
      bug.body.position.y = Math.sin(time * (speaking ? 5 : 1.6)) * (speaking ? .045 : .015);
      bug.legs.forEach((leg, j) => { leg.rotation.y = walking ? Math.sin(time * 9 + j * Math.PI) * .18 : 0; });
      if (speaking) this.speech = { name: i === 0 ? 'Pip' : 'Bean', text: line[1], position: bug.group.position.clone().add(new THREE.Vector3(0, 1.6, 0)) };
    });
    const flyer = this.pairs[1][0];
    const shore = LOTUS_LANDINGS[0];
    const cycle = time % 32; const leg = Math.floor(cycle / 8); const phase = cycle % 8;
    const a = LOTUS_LANDINGS[leg]; const b = LOTUS_LANDINGS[(leg + 1) % 4];
    const t = THREE.MathUtils.smoothstep(phase, 2, 8);
    let target = new THREE.Vector3(THREE.MathUtils.lerp(a.x, b.x, t), .18 + Math.sin(t * Math.PI) * 2.5, THREE.MathUtils.lerp(a.z, b.z, t));
    const escorting = time < this.escortUntil && Math.hypot(x - shore.x, z - shore.z) < 12;
    if (escorting) target.set(x + 1.6, 1.3, z + 1);
    else if (nearest === 1) target.set(shore.x, .18, shore.z);
    const delta = target.clone().sub(flyer.group.position);
    if (delta.length() > .02) { flyer.group.rotation.y = Math.atan2(-delta.x, -delta.z); flyer.group.position.add(delta.multiplyScalar(Math.min(1, dt * 3))); }
    const flying = flyer.group.position.y > .35 || delta.length() > .08;
    this.state.flyerMode = escorting ? 'accompanying' : flying ? 'flying' : 'perched';
    flyer.wings.forEach((wing, i) => { wing.visible = flying; wing.rotation.z = (i ? 1 : -1) * (.3 + Math.sin(time * 35) * .5); });
    flyer.legs.forEach(leg => { leg.rotation.y = flying ? .4 : 0; });
    flyer.body.rotation.x = flying ? .12 : 0;
    const collector = this.pairs[2][0]; const p = INSECT_HANGOUTS[2];
    const seedPhase = time % 24;
    this.state.collectorMode = nearest === 2 ? 'chatting' : seedPhase < 8 ? 'rolling' : seedPhase < 12 ? 'inspecting' : seedPhase < 18 ? 'resting' : 'watching butterfly';
    if (nearest !== 2) {
      const destination = p.x + Math.sin(time / 8) * 2;
      if (seedPhase < 8) collector.group.position.x += (destination - collector.group.position.x) * Math.min(1, dt * 2);
      collector.group.rotation.y = seedPhase < 18 ? Math.PI / 2 : -.6 + Math.sin(time * .3) * .3;
      if (seedPhase < 8) { this.seedObject.position.set(collector.group.position.x - 1.1, .32, p.z); this.seedObject.rotation.z -= dt * 2; }
    } else collector.group.rotation.y = Math.atan2(collector.group.position.x - x, collector.group.position.z - z);
    collector.legs.forEach((leg, i) => { leg.rotation.y = this.state.collectorMode === 'rolling' ? Math.sin(time * 9 + i * Math.PI) * .18 : 0; });
    collector.body.position.y = Math.sin(time * 2) * .025;
    if (nearest > 0 && time >= this.nextChat && (nearest === 2 || !flying)) this.startSolo(nearest);
    const soloLine = nearest > 0 ? this.soloLines[Math.floor((time - this.soloBegan) / 3.8)] : undefined;
    if (soloLine) this.speech = { name: nearest === 1 ? 'Dot' : 'Pebble', text: soloLine, position: this.pairs[nearest][0].group.position.clone().add(new THREE.Vector3(0, 1.6, 0)) };
    if (this.escortPending && time >= this.soloBegan + this.soloLines.length * 3.8) { this.escortPending = false; this.escortUntil = time + 8; }
  }
}
